type SupportedSizes = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

/**
 * list to the window resize and compute various reactive flags and values
 *
 * `isXs`, `isSm`, `isMd`, `isLg`, `isXl`, `is2xl` are reactive flags
 *
 * `currentWidth` is the current window width and `currentSize` is the current size 'xl', 'lg', etc..
 *
 * `watch(currentSize, (oldValue, newValue) => {})` can be used in other components to run callbacks.
 */
class ResizeListener {
  public readonly currentWidth: Ref<number>;

  public readonly currentSize: Ref<SupportedSizes>;

  /** default */
  public readonly xs: number;
  public readonly isXs: Readonly<Ref<boolean>>;

  /** sm breakpoint */
  public readonly sm: number;
  public readonly isSm: Readonly<Ref<boolean>>;

  /** md breakpoint */
  public readonly md: number;
  public readonly isMd: Readonly<Ref<boolean>>;

  /** lg breakpoint */
  public readonly lg: number;
  public readonly isLg: Readonly<Ref<boolean>>;

  /** xl breakpoint */
  public readonly xl: number;
  public readonly isXl: Readonly<Ref<boolean>>;

  /** xxl breakpoint */
  public readonly xxl: number;
  public readonly is2xl: Readonly<Ref<boolean>>;

  public readonly isTabletOrMobile: Readonly<Ref<boolean>>;
  public readonly isMobile: Readonly<Ref<boolean>>;

  constructor({
    sm,
    md,
    lg,
    xl,
    xxl,
  }: {
    sm: number;
    md: number;
    lg: number;
    xl: number;
    xxl: number;
  }) {
    this.xs = 0;
    this.isXs = computed(() => true);

    this.sm = sm;
    this.isSm = computed(() => this.currentSize.value === 'sm' || this.isMd.value);

    this.md = md;
    this.isMd = computed(() => this.currentSize.value === 'md' || this.isLg.value);

    this.lg = lg;
    this.isLg = computed(() => this.currentSize.value === 'lg' || this.isXl.value);

    this.xl = xl;
    this.isXl = computed(() => this.currentSize.value === 'xl' || this.is2xl.value);

    this.xxl = xxl;
    this.is2xl = computed(() => this.currentWidth.value >= this.xxl);

    this.isTabletOrMobile = computed(() => {
      return (
        this.currentSize.value === 'md' ||
        this.currentSize.value === 'sm' ||
        this.currentSize.value === 'xs'
      );
    });
    this.isMobile = computed(() => {
      return this.currentSize.value === 'sm' || this.currentSize.value === 'xs';
    });

    const initWidth = window ? window.innerWidth : 1280;
    this.currentWidth = ref(initWidth);

    const initSize = computed(() => {
      if (!window) return 'xl';
      return this.calculateCurrentSize(this.currentWidth.value);
    });
    this.currentSize = ref(initSize);

    if (!window) {
      return;
    }

    this.currentWidth.value = window.innerWidth;
    window.addEventListener('resize', () => {
      this.currentWidth.value = window.innerWidth;
    });
  }

  private calculateCurrentSize(cw: number): SupportedSizes {
    if (cw < this.sm) return 'xs';
    if (cw < this.md) return 'sm';
    if (cw < this.lg) return 'md';
    if (cw < this.xl) return 'lg';
    if (cw < this.xxl) return 'xl';
    return '2xl';
  }
}

/** Default resizer, the size are the breakpoints */
const defaultResizeListener = new ResizeListener({
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  xxl: 1536,
});

/**
 * Used to create a collection of useful reactive properties to handle
 * responsive design following paradigms from TailwindCSS
 */
export const useResponsive = () => {
  /** Get the default resize listener, we could add other less standard listeners in the future */
  const getDefault = () => {
    return defaultResizeListener;
  };

  return {
    getDefault,
  };
};
